import React from "react";
import { maVyvojInvesticiePoplatky, vytvorRiadkyVyvojaInvesticie, } from "../../utils/investVyvojPomocne";
import { exportujInvesticnyVyvojCsv, exportujInvesticnyVyvojPdf, } from "../../utils/exportInvesticnyVyvoj";

const MAX_ROKOV_BEZ_STRANKOVANIA =
  30;

const POCET_MESIACOV_NA_STRANU_PC =
  24;

const POCET_MESIACOV_NA_STRANU_MOBIL =
  12;

const POCET_ROKOV_NA_STRANU_PC =
  30;

const POCET_ROKOV_NA_STRANU_MOBIL =
  15;

const MOBILNY_BREAKPOINT =
  640;

const INVESTICNE_VARIANTY =
  [1, 2];

function formatujSumu(hodnota) {
  const cislo =
    Number(hodnota);

  if (
    !Number.isFinite(cislo)
  ) {
    return "—";
  }

  return `${
    cislo.toLocaleString(
      "sk-SK",
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }
    )
  } €`;
}

function formatujSumuBezDesatin(
  hodnota
) {
  const cislo =
    Number(hodnota);

  if (
    !Number.isFinite(cislo)
  ) {
    return "—";
  }

  return `${
    cislo.toLocaleString(
      "sk-SK",
      {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }
    )
  } €`;
}

function formatujVkladyDetail(
  riadok
) {
  const privklad =
    Math.max(
      0,
      Number(
        riadok?.privklad
      ) || 0
    );

  if (privklad <= 0) {
    return "";
  }

  return (
    `(prívklad ${
      formatujSumuBezDesatin(
        privklad
      )
    })`
  );
}

function pocetRiadkovNaStranu(
  rezim,
  jeMobil
) {
  if (rezim === "roky") {
    return jeMobil
      ? POCET_ROKOV_NA_STRANU_MOBIL
      : POCET_ROKOV_NA_STRANU_PC;
  }

  return jeMobil
    ? POCET_MESIACOV_NA_STRANU_MOBIL
    : POCET_MESIACOV_NA_STRANU_PC;
}

export default function InvesticneVyvojInvesticieKarta({
  vyvojInvesticieRezim,
  vysledkyInvesticii,
  nastaveniaVariantov,
  porovnanieInvesticii,
  aktivnyInvesticnyVariant,
}) {
  const [
    zobrazenyVariant,
    setZobrazenyVariant,
  ] = React.useState(
    aktivnyInvesticnyVariant === 2
      ? 2
      : 1
  );

  const [
    zobrazitVsetko,
    setZobrazitVsetko,
  ] = React.useState(false);

  const [
    aktualnaStrana,
    setAktualnaStrana,
  ] = React.useState(1);

  const [
    jeMobil,
    setJeMobil,
  ] = React.useState(() =>
    typeof window !== "undefined"
      ? window.innerWidth <=
        MOBILNY_BREAKPOINT
      : false
  );

  React.useEffect(() => {
    const aktualizujRozlisenie =
      () => {
        setJeMobil(
          window.innerWidth <=
            MOBILNY_BREAKPOINT
        );
      };

    aktualizujRozlisenie();

    window.addEventListener(
      "resize",
      aktualizujRozlisenie
    );

    return () =>
      window.removeEventListener(
        "resize",
        aktualizujRozlisenie
      );
  }, []);

  React.useEffect(() => {
    setZobrazenyVariant(
      porovnanieInvesticii &&
        aktivnyInvesticnyVariant ===
          2
        ? 2
        : 1
    );
  }, [
    vyvojInvesticieRezim,
    porovnanieInvesticii,
    aktivnyInvesticnyVariant,
  ]);

  const aktivnyZobrazenyVariant =
    porovnanieInvesticii &&
    zobrazenyVariant === 2
      ? 2
      : 1;

  const vysledok =
    aktivnyZobrazenyVariant === 2
      ? vysledkyInvesticii
          ?.variant2
      : vysledkyInvesticii
          ?.variant1;

  const nastavenia =
    aktivnyZobrazenyVariant === 2
      ? nastaveniaVariantov
          ?.variant2
      : nastaveniaVariantov
          ?.variant1;

  const riadky =
    React.useMemo(
      () =>
        vytvorRiadkyVyvojaInvesticie({
          vysledok,
          nastavenia,
          rezim:
            vyvojInvesticieRezim,
        }),
      [
        vysledok,
        nastavenia,
        vyvojInvesticieRezim,
      ]
    );

  const zobrazitRealnuHodnotu =
    Number(
      vysledok?.inflacia
    ) > 0;

  const zobrazitPoplatky =
    maVyvojInvesticiePoplatky(
      riadky
    );

  const limitRiadkov =
    pocetRiadkovNaStranu(
      vyvojInvesticieRezim,
      jeMobil
    );

  const jeRocneStrankovanie =
    vyvojInvesticieRezim ===
      "roky" &&
    riadky.length >
      MAX_ROKOV_BEZ_STRANKOVANIA;

  const jeMesacneStrankovanie =
    vyvojInvesticieRezim ===
      "mesiace" &&
    riadky.length >
      limitRiadkov;

  const jeStrankovanieAktivne =
    jeRocneStrankovanie ||
    jeMesacneStrankovanie;

  const celkovyPocetStran =
    jeStrankovanieAktivne &&
    !zobrazitVsetko
      ? Math.max(
          1,
          Math.ceil(
            riadky.length /
              limitRiadkov
          )
        )
      : 1;

  React.useEffect(() => {
    setAktualnaStrana(1);
  }, [
    vyvojInvesticieRezim,
    aktivnyZobrazenyVariant,
    zobrazitVsetko,
    limitRiadkov,
    riadky.length,
  ]);

  React.useEffect(() => {
    if (
      aktualnaStrana >
      celkovyPocetStran
    ) {
      setAktualnaStrana(
        celkovyPocetStran
      );
    }
  }, [
    aktualnaStrana,
    celkovyPocetStran,
  ]);

  const zobrazeneRiadky =
    React.useMemo(() => {
      if (
        !jeStrankovanieAktivne ||
        zobrazitVsetko
      ) {
        return riadky;
      }

      const od =
        (
          aktualnaStrana - 1
        ) * limitRiadkov;

      return riadky.slice(
        od,
        od + limitRiadkov
      );
    }, [
      riadky,
      jeStrankovanieAktivne,
      zobrazitVsetko,
      aktualnaStrana,
      limitRiadkov,
    ]);

  if (
    !vyvojInvesticieRezim
  ) {
    return null;
  }

  const exportneParametre = {
    riadky,
    rezim:
      vyvojInvesticieRezim,
    variant:
      aktivnyZobrazenyVariant,
    zobrazitRealnuHodnotu,
    zobrazitPoplatky,
  };

  const zobrazitDan =
    Number(
      vysledok?.danZVynosu
    ) > 0;

  const zobrazitPoznamkuDodatocnehoZhodnocovania =
    riadky.some(
      (riadok) =>
        riadok
          .jeZaciatokDodatocnehoZhodnocovania
    );

  const zobrazitSuhrn =
    zobrazitDan ||
    zobrazitPoznamkuDodatocnehoZhodnocovania;

  return (
    <div className="card-section card--full invest-vyvoj-card">
      <div className="prepay-head invest-vyvoj-head">
        <h2>
          Vývoj investície
        </h2>

        {porovnanieInvesticii && (
          <div
            className="applicant-tabs"
            role="group"
            aria-label="Variant vývoja investície"
          >
            {INVESTICNE_VARIANTY.map(
              (variant) => (
                <button
                  key={variant}
                  type="button"
                  className={
                    `applicant-tab ${
                      aktivnyZobrazenyVariant ===
                      variant
                        ? "is-active"
                        : ""
                    }`
                  }
                  aria-pressed={
                    aktivnyZobrazenyVariant ===
                    variant
                  }
                  onClick={() =>
                    setZobrazenyVariant(
                      variant
                    )
                  }
                >
                  Variant {variant}
                </button>
              )
            )}
          </div>
        )}
      </div>

      <div className="schedule-wrap invest-vyvoj-wrap">
        <div className="schedule-actions">
          <span className="ltv-caption">
            Export:
          </span>

          <button
            type="button"
            className="ltv-btn"
            onClick={() =>
              exportujInvesticnyVyvojCsv(
                exportneParametre
              )
            }
          >
            CSV
          </button>

          <button
            type="button"
            className="ltv-btn"
            onClick={() =>
              exportujInvesticnyVyvojPdf({
                ...exportneParametre,
                danZVynosu:
                  vysledok
                    ?.danZVynosu,
                konecnaHodnotaPoDani:
                  vysledok
                    ?.konecnaHodnotaPoDani,
              })
            }
          >
            PDF
          </button>
        </div>

        {jeStrankovanieAktivne && (
          <div className="schedule-pagination">
            <div className="schedule-pagination__left">
              <label className="chk schedule-pagination__toggle">
                <input
                  type="checkbox"
                  checked={
                    zobrazitVsetko
                  }
                  onChange={(
                    event
                  ) =>
                    setZobrazitVsetko(
                      event.target
                        .checked
                    )
                  }
                />

                Zobraziť všetko
              </label>
            </div>

            {!zobrazitVsetko && (
              <div className="schedule-pagination__right">
                <button
                  type="button"
                  className="ltv-btn"
                  disabled={
                    aktualnaStrana <=
                    1
                  }
                  onClick={() =>
                    setAktualnaStrana(
                      (strana) =>
                        Math.max(
                          1,
                          strana - 1
                        )
                    )
                  }
                >
                  Predchádzajúca
                </button>

                <span className="schedule-pagination__info">
                  Strana{" "}
                  {aktualnaStrana} /{" "}
                  {celkovyPocetStran}
                </span>

                <button
                  type="button"
                  className="ltv-btn"
                  disabled={
                    aktualnaStrana >=
                    celkovyPocetStran
                  }
                  onClick={() =>
                    setAktualnaStrana(
                      (strana) =>
                        Math.min(
                          celkovyPocetStran,
                          strana + 1
                        )
                    )
                  }
                >
                  Ďalšia
                </button>
              </div>
            )}
          </div>
        )}

        <table
          className={
            `schedule invest-vyvoj-table ${
              zobrazitPoplatky
                ? ""
                : "is-without-fees"
            }`
          }
        >
          <thead>
            <tr>
              <th>
                {vyvojInvesticieRezim ===
                "roky"
                  ? "Rok"
                  : "Mesiac"}
              </th>

              <th>Vklady</th>
              <th>Zhodnotenie</th>

              {zobrazitPoplatky && (
                <>
                  <th>Poplatky</th>
                  <th>
                    Vplyv poplatkov
                  </th>
                </>
              )}

              <th>
                Hodnota investície
              </th>

              {zobrazitRealnuHodnotu && (
                <th>
                  Reálna hodnota
                </th>
              )}
            </tr>
          </thead>

          <tbody>
            {zobrazeneRiadky.map(
              (riadok) => {
                const vkladyDetail =
                  formatujVkladyDetail(
                    riadok
                  );

                return (
                  <tr
                    key={
                      riadok.key
                    }
                    className={
                      riadok
                        .jeZaciatokDodatocnehoZhodnocovania
                        ? "invest-vyvoj-table__additional-start"
                        : ""
                    }
                  >
                    <td>
                      <span>
                        {
                          riadok.obdobie
                        }

                        {riadok
                          .jeZaciatokDodatocnehoZhodnocovania &&
                          "*"}
                      </span>
                    </td>

                    <td>
                      <span>
                        {formatujSumu(
                          riadok.vklady
                        )}
                      </span>

                      {vkladyDetail && (
                        <span className="invest-vyvoj-table__detail">
                          {
                            vkladyDetail
                          }
                        </span>
                      )}
                    </td>

                    <td>
                      {formatujSumu(
                        riadok
                          .zhodnotenie
                      )}
                    </td>

                    {zobrazitPoplatky && (
                      <>
                        <td>
                          <span>
                            {formatujSumu(
                              riadok
                                .poplatky
                            )}
                          </span>

                          {riadok
                            .externyPoplatok >
                            0 && (
                            <span className="invest-vyvoj-table__detail">
                              z toho mimo
                              investície{" "}
                              {formatujSumu(
                                riadok
                                  .externyPoplatok
                              )}
                            </span>
                          )}
                        </td>

                        <td>
                          {formatujSumu(
                            riadok
                              .vplyvPoplatkov
                          )}
                        </td>
                      </>
                    )}

                    <td>
                      {formatujSumu(
                        riadok
                          .hodnotaInvesticie
                      )}
                    </td>

                    {zobrazitRealnuHodnotu && (
                      <td>
                        {formatujSumu(
                          riadok
                            .realnaHodnota
                        )}
                      </td>
                    )}
                  </tr>
                );
              }
            )}
          </tbody>
        </table>
      </div>

      {zobrazitSuhrn && (
        <div className="invest-vyvoj-summary">
          {zobrazitPoznamkuDodatocnehoZhodnocovania && (
            <strong className="invest-vyvoj-summary__note">
              *Dodatočné zhodnocovanie
              bez vkladov
            </strong>
          )}

          {zobrazitDan && (
            <div className="invest-vyvoj-summary__right">
              <span>
                Odhadovaná daň pri
                ukončení:{" "}
                <strong>
                  {formatujSumu(
                    vysledok
                      ?.danZVynosu
                  )}
                </strong>
              </span>

              <span>
                Konečná hodnota po
                zdanení:{" "}
                <strong>
                  {formatujSumu(
                    vysledok
                      ?.konecnaHodnotaPoDani
                  )}
                </strong>
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}